/**
 * BLOCK ORDER (spec docs/SPEC-PROMO-BLOCK-ORDER.md, 2026-10-20) — the ordered
 * list of CONTENT block ids of the public promo page:
 *   `intro` | `bio` | `quotes` | `socials` | `contact` | `section:<key>`
 * The hero is NEVER in the list: always first, not movable. The blob key
 * `blockOrder` is additive and optional (absent = canonical order, strict
 * backcompat with the prod blobs); unknown / duplicate ids are dropped, and
 * every canonical id missing from the list is appended after the listed ones
 * in canonical relative order — the resolution is always a TOTAL order.
 * Mirror of `server/lib/promo-content.js` (canonicalBlockIds / resolveBlockOrder).
 */
import type { PromoContentSection } from '../api';

// CANONICAL_BLOCKS REMOVED (audit 2026-10-24 [DEAD]): the source of truth is the
// server mirror (server/lib/promo-content.js canonicalBlockIds) — the client
// constant had no reader.
export type PromoBlockId = string;

/** Sections in their canonical (position) order. */
const sortedSections = (sections: PromoContentSection[]) =>
  [...(sections || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

/** Canonical block ids for a blob's sections. */
export function canonicalBlockIds(sections: PromoContentSection[]): PromoBlockId[] {
  const secIds = sortedSections(sections).map((s) => `section:${s.key}`);
  return ['intro', 'bio', 'quotes', ...secIds, 'socials', 'contact'];
}

/** Resolve the blob's `blockOrder` against the known ids → total order. */
export function resolveBlockOrder(
  blockOrder: string[] | undefined,
  sections: PromoContentSection[],
): PromoBlockId[] {
  const canonical = canonicalBlockIds(sections);
  const known = new Set(canonical);
  const listed: PromoBlockId[] = [];
  if (Array.isArray(blockOrder)) {
    for (const raw of blockOrder) {
      const id = String(raw ?? '').trim();
      if (id && known.has(id) && !listed.includes(id) && listed.length < 20) listed.push(id);
    }
  }
  return [...listed, ...canonical.filter((id) => !listed.includes(id))];
}
